import { TFunction } from "i18next";
import React from "react";
import { useTranslation } from "react-i18next";
import { FormFieldRule, IFormInstance, FormFieldRuleObject } from "../types";
import moment from "moment";
import { TimeHelper } from "@xala/common-services";

import { REGEX } from "../../../";

interface IValidatorParams {
  choiceField?: boolean;
  message?: string;
}

const isUnwrappedForm = (
  form: React.RefObject<IFormInstance> | IFormInstance
): form is IFormInstance =>
  (form as IFormInstance).getFieldsValue !== undefined;

const makeValidators = (t: TFunction) => ({
  phone: (): FormFieldRule[] => [
    {
      message: t("PHONE_VALIDATION_MESSAGE", "The phone number is not valid"),
      pattern: REGEX.PHONE,
    },
  ],

  internationalPhone: (): FormFieldRule[] => [
    {
      validator: async (_, value) => {
        if (value && !value.startsWith("+")) {
          throw t(
            "USER_PHONE_NUMBER_PLUS_VALIDATION_MESSAGE",
            'Valid phone number starts with "+"'
          );
        }
        if (value && value.length > 20) {
          throw t(
            "USER_PHONE_NUMBER_LENGTH_VALIDATION_MESSAGE",
            "Phone number cannot be longer than 20 characters"
          );
        }
      },
    },
  ],

  isBefore: (
    form: React.RefObject<IFormInstance> | IFormInstance,
    startDate: string,
    endDate: string,
    messageKey: string
  ): FormFieldRule[] => [
    {
      validator: (_: FormFieldRuleObject, value: string) => {
        const normalizedForm = isUnwrappedForm(form) ? form : form.current;
        if (!value || !form || !normalizedForm?.getFieldValue(endDate)) {
          return Promise.resolve();
        }
        return moment(normalizedForm?.getFieldValue(startDate)).isBefore(
          normalizedForm?.getFieldValue(endDate)
        ) || normalizedForm?.getFieldValue(endDate) === undefined
          ? Promise.resolve()
          : Promise.reject(t(messageKey));
      },
    },
  ],

  isAfter: (
    form: React.RefObject<IFormInstance> | IFormInstance,
    startDate: string,
    endDate: string,
    messageKey: string
  ): FormFieldRule[] => [
    {
      validator: (_: FormFieldRuleObject, value: string) => {
        const normalizedForm = isUnwrappedForm(form) ? form : form.current;
        if (!value || !form || !normalizedForm?.getFieldValue(startDate)) {
          return Promise.resolve();
        }
        return moment(normalizedForm?.getFieldValue(endDate)).isAfter(
          normalizedForm?.getFieldValue(startDate)
        )
          ? Promise.resolve()
          : Promise.reject(t(messageKey));
      },
    },
  ],

  compareIsBefore: (
    form: React.RefObject<IFormInstance> | IFormInstance,
    startDate: string,
    endDate: string,
    messageKey: string
  ): FormFieldRule[] => [
    {
      validator: (_: FormFieldRuleObject, value: string) => {
        const normalizedForm = isUnwrappedForm(form) ? form : form.current;

        if (!value || !form || !normalizedForm?.getFieldValue(endDate)) {
          return Promise.resolve();
        }
        return TimeHelper.compareIsBefore(
          TimeHelper.parse(normalizedForm?.getFieldValue(startDate)),
          TimeHelper.parse(normalizedForm?.getFieldValue(endDate))
        ) || normalizedForm?.getFieldValue(endDate) === undefined
          ? Promise.resolve()
          : Promise.reject(t(messageKey));
      },
    },
  ],

  compareIsAfter: (
    form: React.RefObject<IFormInstance> | IFormInstance,
    startDate: string,
    endDate: string,
    messageKey: string
  ): FormFieldRule[] => [
    {
      validator: (_: FormFieldRuleObject, value: string) => {
        const normalizedForm = isUnwrappedForm(form) ? form : form.current;

        if (!value || !form || !normalizedForm?.getFieldValue(startDate)) {
          return Promise.resolve();
        }
        return TimeHelper.compareIsAfter(
          TimeHelper.parse(normalizedForm?.getFieldValue(endDate)),
          TimeHelper.parse(normalizedForm?.getFieldValue(startDate))
        )
          ? Promise.resolve()
          : Promise.reject(t(messageKey));
      },
    },
  ],

  required: ({
    message,
    choiceField,
  }: IValidatorParams = {}): FormFieldRule[] => [
    {
      required: true,
      // Workaround for Choose field validation crashing against any value of whitespace prop
      ...(choiceField ? {} : { whitespace: true }),
      message:
        message || t("REQUIRED_VALIDATION_MESSAGE", "This field is required"),
    },
  ],

  password: (): FormFieldRule[] => [
    {
      message: t(
        "PASSWORD_LENGTH_VALIDATION_MESSAGE",
        "The password should be at least 8 characters long"
      ),
      min: 8,
    },
    {
      message: t(
        "PASSWORD_LETTERS_VALIDATION_MESSAGE",
        "The password should contain one uppercase & lowercase letter"
      ),
      pattern: REGEX.PASSWORD,
    },
  ],

  passwordConfirm: (
    form: React.RefObject<IFormInstance> | IFormInstance,
    fieldName = "Password"
  ): FormFieldRule[] => [
    {
      validator: async (_, value) => {
        if (!form) {
          return;
        }
        const normalizedForm = isUnwrappedForm(form) ? form : form.current;
        if (!normalizedForm) {
          throw t("PASSWORD_CONFIRM_VALIDATION_ERROR", "Error");
        }

        if (value && value !== normalizedForm.getFieldValue(fieldName)) {
          throw t(
            "PASSWORD_CONFIRM_VALIDATION_MESSAGE",
            "Two passwords that you enter is inconsistent"
          );
        }
      },
    },
  ],

  email: (): FormFieldRule[] => [
    {
      message: t("EMAIL_VALIDATION_MESSAGE", "The input is not valid E-mail"),
      type: "email",
    },
  ],

  positiveInteger: (): FormFieldRule[] => [
    {
      pattern: REGEX.POSITIVE_INTEGER,
      message: t(
        "POSITIVE_INTEGER_VALIDATION_MESSAGE",
        "Must be positive integer"
      ),
    },
  ],

  maxItems: (limit: number): FormFieldRule[] => [
    {
      validator: async (_, value) => {
        if (value && value.length > limit) {
          throw t("MAX_ITEMS_VALIDATION_MESSAGE", { limit: limit });
        }
      },
    },
  ],

  isGreaterOrEqual: (
    initialValue: number,
    messageKey: string
  ): FormFieldRule[] => [
    {
      validator: (_: FormFieldRuleObject, value: string | number) => {
        if (Number(value) >= initialValue) {
          return Promise.resolve();
        }

        return Promise.reject(t(messageKey));
      },
    },
  ],
});

export function useValidate() {
  const [t] = useTranslation();

  return React.useMemo(() => makeValidators(t), [t]);
}
